import type { PublishedFeedItem } from '@/lib/platforms/instagram-feed-mapper';

export type InstagramPlannedPlacement = 'feed' | 'reel' | 'story';

export interface PlannedInstagramItem {
  id: string;
  postId: string;
  revisionId: string;
  connectionId: string;
  kind: 'planned';
  placement: InstagramPlannedPlacement;
  mediaUrl: string | null;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  profilePlanRank: string | null;
  scheduledAt: string | null;
  approvalStatus: string;
  deliveryStatus: string;
  nativePostId: string | null;
  shareToFeed: boolean;
  hasProfileCrop: boolean;
  focalX: number;
  focalY: number;
  updatedAt: string;
}

export interface NativeInstagramStoryItem {
  id: string;
  connectionId: string;
  kind: 'native-story';
  mediaUrl: string;
  permalink: string | null;
  mediaType: 'IMAGE' | 'VIDEO';
  publishedAt: string;
  expiresAt: string;
}

export type InstagramProfileTile = PublishedFeedItem | PlannedInstagramItem;
export type InstagramStoryRailItem = NativeInstagramStoryItem | PlannedInstagramItem;

export interface SocialTargetPlanRow {
  id: string;
  post_id: string;
  revision_id: string;
  connection_id: string;
  cover_asset_id?: string | null;
  cover_asset_url?: string | null;
  profile_plan_rank: string | null;
  placement: string;
  native_options_snapshot: unknown;
  scheduled_at: string | null;
  approval_status: string;
  delivery_status: string;
  native_post_id: string | null;
  updated_at: string;
}

export interface SocialRevisionPlanRow {
  id: string;
  media_snapshot: unknown;
}

export interface NativeStoryPlanRow {
  connection_id: string;
  native_story_id: string;
  media_type: string;
  thumbnail_path: string;
  permalink: string | null;
  published_at: string;
  expires_at: string;
}

type JsonRecord = Record<string, unknown>;

function recordValue(value: unknown): JsonRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;
}

function finiteUnitValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
}

function stringFrom(record: JsonRecord | null, keys: string[]): string | null {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function firstMediaSnapshot(value: unknown): { item: unknown; count: number } {
  if (!Array.isArray(value) || value.length === 0) return { item: null, count: 0 };
  return { item: value[0], count: value.length };
}

function mediaTypeFor(item: unknown, count: number): PlannedInstagramItem['mediaType'] {
  if (count > 1) return 'CAROUSEL_ALBUM';
  const record = recordValue(item);
  const rawType = stringFrom(record, ['mediaType', 'media_type', 'kind', 'type'])?.toLowerCase();
  if (rawType?.includes('video') || rawType?.includes('reel')) return 'VIDEO';
  const url =
    typeof item === 'string' ? item : stringFrom(record, ['url', 'mediaUrl', 'media_url']);
  return url && /\.(mp4|mov|m4v|webm)(?:$|\?)/i.test(url) ? 'VIDEO' : 'IMAGE';
}

function plannedMediaUrl(mediaSnapshot: unknown, nativeOptions: JsonRecord | null): string | null {
  const cover = stringFrom(nativeOptions, ['coverImageUrl', 'cover_image_url']);
  if (cover) return cover;
  const { item } = firstMediaSnapshot(mediaSnapshot);
  if (typeof item === 'string' && item.trim()) return item;
  return stringFrom(recordValue(item), [
    'thumbnailUrl',
    'thumbnail_url',
    'coverUrl',
    'cover_url',
    'mediaUrl',
    'media_url',
    'url',
  ]);
}

export function mapTargetToPlannedInstagramItem(
  target: SocialTargetPlanRow,
  revision: SocialRevisionPlanRow | undefined,
): PlannedInstagramItem | null {
  if (!['feed', 'reel', 'story'].includes(target.placement)) return null;
  const nativeOptions = recordValue(target.native_options_snapshot);
  const profileSurface = recordValue(
    nativeOptions?.profileSurface ?? nativeOptions?.profile_surface,
  );
  const profileCrop = recordValue(
    profileSurface ?? nativeOptions?.profileCrop ?? nativeOptions?.profile_crop,
  );
  const profileCropRatio = stringFrom(profileCrop, ['ratio']);
  const profileCropFocalX = profileCrop?.focalX ?? profileCrop?.focal_x;
  const profileCropFocalY = profileCrop?.focalY ?? profileCrop?.focal_y;
  const profileCropZoom = profileCrop?.zoom;
  const hasProfileCrop = Boolean(
    profileCropRatio &&
    typeof profileCropFocalX === 'number' &&
    Number.isFinite(profileCropFocalX) &&
    typeof profileCropFocalY === 'number' &&
    Number.isFinite(profileCropFocalY) &&
    typeof profileCropZoom === 'number' &&
    Number.isFinite(profileCropZoom),
  );
  const mediaSnapshot = revision?.media_snapshot;
  const { item, count } = firstMediaSnapshot(mediaSnapshot);

  return {
    id: target.id,
    postId: target.post_id,
    revisionId: target.revision_id,
    connectionId: target.connection_id,
    kind: 'planned',
    placement: target.placement as InstagramPlannedPlacement,
    mediaUrl: target.cover_asset_url ?? plannedMediaUrl(mediaSnapshot, nativeOptions),
    mediaType: mediaTypeFor(item, count),
    profilePlanRank: target.profile_plan_rank,
    scheduledAt: target.scheduled_at,
    approvalStatus: target.approval_status,
    deliveryStatus: target.delivery_status,
    nativePostId: target.native_post_id,
    shareToFeed: nativeOptions?.shareToFeed === true || nativeOptions?.share_to_feed === true,
    hasProfileCrop,
    focalX: finiteUnitValue(profileCropFocalX, 0.5),
    focalY: finiteUnitValue(profileCropFocalY, 0.5),
    updatedAt: target.updated_at,
  };
}

function comparePlannedProfileOrder(a: PlannedInstagramItem, b: PlannedInstagramItem): number {
  if (a.profilePlanRank && b.profilePlanRank) {
    const rankOrder =
      a.profilePlanRank < b.profilePlanRank ? -1 : a.profilePlanRank > b.profilePlanRank ? 1 : 0;
    if (rankOrder !== 0) return rankOrder;
  } else if (a.profilePlanRank) {
    return -1;
  } else if (b.profilePlanRank) {
    return 1;
  }

  if (a.scheduledAt && b.scheduledAt) {
    const scheduledOrder =
      a.scheduledAt < b.scheduledAt ? 1 : a.scheduledAt > b.scheduledAt ? -1 : 0;
    if (scheduledOrder !== 0) return scheduledOrder;
  } else if (a.scheduledAt) {
    return -1;
  } else if (b.scheduledAt) {
    return 1;
  }

  const updatedOrder = a.updatedAt.localeCompare(b.updatedAt);
  return updatedOrder !== 0 ? updatedOrder : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export const INSTAGRAM_PUBLISHED_SYNC_GRACE_MS = 24 * 60 * 60 * 1000;

function isPublishedFallbackCurrent(item: PlannedInstagramItem, nowMs: number): boolean {
  if (!['published', 'published_with_warnings'].includes(item.deliveryStatus)) return true;
  const updatedAt = new Date(item.updatedAt).getTime();
  return Number.isFinite(updatedAt) && nowMs - updatedAt <= INSTAGRAM_PUBLISHED_SYNC_GRACE_MS;
}

/**
 * Produces a truthful read-only projection. Native history wins once a target's
 * provider id appears, Stories stay outside profile order, and unplaced targets
 * remain outside the grid until the staging tray lands.
 */
export function mergeInstagramProfileTiles(
  nativeItems: PublishedFeedItem[],
  plannedItems: PlannedInstagramItem[],
  nowMs: number = Date.now(),
): InstagramProfileTile[] {
  const nativeIds = new Set(nativeItems.map((item) => item.id));
  const planned = plannedItems
    .filter((item) => item.placement !== 'story')
    .filter((item) => item.placement !== 'reel' || (item.shareToFeed && item.hasProfileCrop))
    .filter((item) => isPublishedFallbackCurrent(item, nowMs))
    .filter((item) => Boolean(item.profilePlanRank || item.scheduledAt))
    .filter((item) => !item.nativePostId || !nativeIds.has(item.nativePostId))
    .sort(comparePlannedProfileOrder);

  const native = [...nativeItems].sort((a, b) => {
    const publishedOrder = b.publishedAt.localeCompare(a.publishedAt);
    if (publishedOrder !== 0) return publishedOrder;
    const sourcedOrder = b.sourcedAt.localeCompare(a.sourcedAt);
    return sourcedOrder !== 0 ? sourcedOrder : a.id.localeCompare(b.id);
  });

  return [...planned, ...native];
}

export function mergeInstagramStoryRail(
  nativeItems: NativeInstagramStoryItem[],
  plannedItems: PlannedInstagramItem[],
  nowMs: number = Date.now(),
): InstagramStoryRailItem[] {
  const activeNativeItems = nativeItems.filter((item) => {
    const expiresAt = new Date(item.expiresAt).getTime();
    return Number.isFinite(expiresAt) && expiresAt > nowMs;
  });
  const nativeIds = new Set(activeNativeItems.map((item) => item.id));
  const plannedStories = plannedItems
    .filter((item) => item.placement === 'story')
    .filter((item) => isPublishedFallbackCurrent(item, nowMs))
    .filter((item) => !item.nativePostId || !nativeIds.has(item.nativePostId))
    .sort((a, b) => {
      if (a.scheduledAt && b.scheduledAt) return a.scheduledAt.localeCompare(b.scheduledAt);
      if (a.scheduledAt) return -1;
      if (b.scheduledAt) return 1;
      return a.id.localeCompare(b.id);
    });

  return [
    ...activeNativeItems.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)),
    ...plannedStories,
  ];
}
